import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Image } from "@/components/ui/image";
import { Section } from "@/components/yts/HomeSections";
import { useLang } from "@/lib/i18n";

/**
 * The invitation that sits after the first rows of results: a short welcome
 * into the wider guide, then the curated collections as a rail the visitor
 * swipes through — the same sideways rhythm as the rest of the page, so a
 * list of results still feels like the guide rather than a search report.
 */
export default function KeepExploring({ count = 0 }) {
  const { lang } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    base44.entities.Collection.filter({ status: "Published", featured: true }, { sort: "title", limit: 12 })
      .then((page) => setItems(page?.items || (Array.isArray(page) ? page : [])))
      .catch(() => setItems([]));
  }, []);

  if (!items.length) return null;

  const copy = lang === "es"
    ? {
      kicker: "Bienvenido a la guía",
      title: "Sigue explorando la isla.",
      line: count
        ? `${count} lugares, más las colecciones curadas de la isla. Desliza para ver a dónde ir después.`
        : "Colecciones curadas de la isla. Desliza para ver a dónde ir después.",
      cta: "Ver todo en el mapa",
      collections: "Colecciones curadas",
    }
    : {
      kicker: "Welcome to the guide",
      title: "Keep exploring the island.",
      line: count
        ? `${count} places, plus the island's curated collections. Swipe to see where to go next.`
        : "The island's curated collections. Swipe to see where to go next.",
      cta: "See everything on the map",
      collections: "Curated collections",
    };

  return (
    <Section>
      <div className="rounded-[18px] border border-[#e7e2d7] bg-[#fdfbf6] px-4 py-4 sm:px-6 sm:py-5">
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#ef3340]">{copy.kicker}</p>
        <h2 className="mt-1.5 text-[22px] font-black leading-[1.06] tracking-[-0.045em] text-[#171717] sm:text-[27px]">{copy.title}</h2>
        <p className="mt-1.5 max-w-[52ch] text-[12.5px] leading-[1.5] text-[#6d6d6a] sm:text-[13.5px]">{copy.line}</p>
        <Link to="/map" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-extrabold text-[#171717] underline decoration-[#ef3340]/50 underline-offset-4">
          {copy.cta} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
        </Link>
      </div>

      <h3 className="mt-5 text-[11px] font-black uppercase tracking-[0.16em] text-[#8b877f]">{copy.collections}</h3>
      <div className="mt-2.5 flex items-stretch gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 scroll-smooth sm:gap-4">
        {items.map((item) => (
          <Link key={item.id} to={`/explore/${item.slug}`} className="group w-[min(66vw,240px)] shrink-0 snap-start">
            <div className="aspect-[4/3] overflow-hidden rounded-[16px] border border-[#deded9] bg-[#e8e8e4]">
              {item.cover_image ? (
                <Image src={item.cover_image} alt={item.title} fittingType="fill" loading="lazy" decoding="async" quality={74} className="h-full w-full object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.03]" />
              ) : (
                <div className="grid h-full w-full place-items-center text-[11px] font-black uppercase tracking-[0.14em] text-[#8d8d88]">{item.title}</div>
              )}
            </div>
            <div className="pt-2">
              <div className="flex items-center gap-1">
                <h4 className="min-w-0 truncate text-[15px] font-black tracking-[-0.025em] text-[#171717]">{item.title}</h4>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-[#8d8d88]" />
              </div>
              {item.dek && <p className="mt-0.5 line-clamp-2 text-[11.5px] leading-[1.4] text-[#6d6d6a]">{item.dek}</p>}
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}